import { UnitGroup, UnitGroupTier } from "../../types/UnitGroup";
import { IUTConfiguration } from "../../types/UTConfiguration";
import { SortData } from "../../utils/Sort";
import formatFloorData from "../../utils/FormatFloor";

export type CustomImage = {
  uuid: string;
  preSignedUrl: string;
  key?: string;
  fileFormat?: string;
  imageType?: string;
  companyAlias?: string | null;
  category?: string | null;
  uploadedBy?: string;
  createdAt?: string;
  isPrimary?: boolean;
  isDeleted?: boolean;
  sequenceNo?: number;
};

export type IUnitImages = {
  customImages: CustomImage[];
  defaultImages: CustomImage[];
};

export type IUnitImageInformation = {
  images?: IUnitImages;
  preserveImageArea?: boolean;
};

export type IUnitsPriceAvailabilityInformation = {
  strikethroughPrice: number | null;
  startPrice: number;
  endPrice?: number;
  availbleUnits: number;
  pricePer: string;
  discountedPriceStart?: number;
  discountedPriceEnd?: number;
  currencySymbol: string;
  fees: UnitGroup["applicableFees"];
  totalApplicableFees: number;
};

export type IUnitAmenitiesInformation = {
  amenities: string[];
  noOfAmenitiesToShow?: number;
};

export interface IUnitsInformation
  extends
    IUnitImageInformation,
    IUnitsPriceAvailabilityInformation,
    IUnitAmenitiesInformation {
  id: string;
  name?: string;
  size: { length: number; width: number; height: number };
  area?: number;
  category?: string;
  unitType?: string;
  tiers: UnitGroupTier[];
  discountPlans: UnitGroup["discountPlans"];
  location: string;
  onSelectClick?: () => void;
  hideDiscountedPrice?: boolean;
}

export const createUnitsGroup = (
  unitGroup: UnitGroup,
  configuration: IUTConfiguration,
): IUnitsInformation => {
  const effectiveUsePromoModifiedPricing =
    configuration.usePromoModifiedPricing ?? true;

  const lowestTier = SortData(
    unitGroup.unitGroupTiers || [],
    (x) => x.discountedPrice || x.price,
    "asc",
  ).find((x) => x.availableUnitsCount > 0);

  if (
    unitGroup?.unitGroupTiers &&
    typeof unitGroup?.unitGroupTiers[0]?.tier?.rank === "number"
  ) {
    SortData(
      unitGroup.unitGroupTiers || [],
      (x) =>
        typeof x?.tier?.rank === "number"
          ? x.tier.rank
          : x.discountedPrice || x.price,
      unitGroup?.unitGroupTiers &&
        typeof unitGroup?.unitGroupTiers[0]?.tier?.rank === "number"
        ? "desc"
        : "asc",
    );
  }
  const hasAutoAppliedDiscount =
    unitGroup.discountPlans?.some((plan) => plan?.autoApply) ?? false;

  const shouldHideDiscountedPrice =
    effectiveUsePromoModifiedPricing === false && hasAutoAppliedDiscount;

  return {
    id: unitGroup.id,
    name: unitGroup.name,
    size: {
      width: unitGroup.width,
      length: unitGroup.length,
      height: unitGroup.height || 0,
    },
    strikethroughPrice: unitGroup?.strikethroughPrice,
    startPrice: (lowestTier || unitGroup).price,
    discountedPriceStart: !shouldHideDiscountedPrice
      ? (lowestTier || unitGroup).discountedPrice
      : undefined,
    totalApplicableFees: unitGroup.totalApplicableFees,
    fees: unitGroup.applicableFees || [],
    // endPrice: unitGroup.discountedPrice,
    availbleUnits: unitGroup.availableUnitsCount,
    pricePer: "mo",
    currencySymbol: "$",
    area: unitGroup?.area || unitGroup?.length * unitGroup?.width,
    amenities: [
      ...(unitGroup?.unitType ? [unitGroup.unitType] : []),
      ...unitGroup.amenities.map((amenity) => amenity.name),
    ],
    images: {
      customImages: unitGroup?.images?.customImages ?? [],
      defaultImages: unitGroup?.images?.defaultImages ?? [],
    },
    tiers: unitGroup.unitGroupTiers || [],
    hideDiscountedPrice: shouldHideDiscountedPrice,
    preserveImageArea: true,
    category: unitGroup.unitCategories?.[0]?.name,
    unitType: unitGroup?.unitType,
    discountPlans: unitGroup.discountPlans || [],
    location: [
      unitGroup.accessType,
      formatFloorData(unitGroup?.floor),
      unitGroup.unitEnclosureStatus,
    ]
      .filter((x) => Boolean(x))
      .join(", "),
  };
};
